import React, { useState, useEffect } from 'react';
import { Globe, CheckCircle, Award, TrendingUp, Clock, MapPin } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Hero = () => {
  const [content, setContent] = useState({
    announcement: "Mumbai's Trusted Visa Consultancy Since 2009",
    title: "Your Gateway to Global Opportunities",
    subtitle: "Expert visa consultation services for travel, study, work, and immigration. 98% success rate with personalized guidance every step of the way.",
    button_primary: "Get Free Consultation",
    button_secondary: "Explore Services",
    features: ["Expert Guidance", "Fast Processing", "High Success Rate"]
  });
  const [backgroundSettings, setBackgroundSettings] = useState({
    hero_image: '',
    hero_opacity: 0.6
  });
  const [stats, setStats] = useState({
    visas_processed: '5000+',
    success_rate: '98%',
    years_experience: '15+',
    countries_covered: '50+'
  });

  useEffect(() => {
    fetchHeroContent();
    fetchBackgroundSettings();
    fetchStats();
  }, []);

  const fetchHeroContent = async () => {
    try {
      const response = await axios.get(`${API}/page-content/home`);
      const heroData = response.data.find(item => item.section_name === 'hero');
      if (heroData && heroData.content) {
        setContent(heroData.content);
      }
    } catch (error) {
      console.error('Failed to fetch hero content:', error);
    }
  };

  const fetchBackgroundSettings = async () => {
    try {
      const response = await axios.get(`${API}/page-content/home`);
      let bgData = response.data.find(item => item.section_name === 'backgrounds_v2');
      if (!bgData) {
        bgData = response.data.find(item => item.section_name === 'backgrounds');
      }
      if (bgData && bgData.content) {
        setBackgroundSettings({
          hero_image: bgData.content.hero_image || '',
          hero_opacity: bgData.content.hero_opacity || 0.6
        });
      }
    } catch (error) {
      console.error('Failed to fetch background settings:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const statsRes = await axios.get(`${API}/settings/stats`);
      setStats(statsRes.data.setting_value);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setTimeout(() => {
        const nameInput = document.getElementById('name');
        if (nameInput) {
          nameInput.focus();
        }
      }, 600);
    }
  };

  const scrollToServices = () => {
    const element = document.getElementById('services');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const statsArray = [
    { 
      number: stats.visas_processed, 
      label: 'Visas Processed',
      icon: Award,
      gradient: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
      bgLight: '#faf5ff'
    },
    { 
      number: stats.success_rate, 
      label: 'Success Rate',
      icon: TrendingUp,
      gradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
      bgLight: '#f0fdf4'
    },
    { 
      number: stats.years_experience, 
      label: 'Years Experience',
      icon: Clock,
      gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
      bgLight: '#fefce8'
    },
    { 
      number: stats.countries_covered, 
      label: 'Countries Covered',
      icon: MapPin,
      gradient: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
      bgLight: '#eff6ff'
    }
  ];

  return (
    <section className="hero-section" style={{
      backgroundImage: backgroundSettings.hero_image ? `url(${backgroundSettings.hero_image})` : 'none',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      position: 'relative'
    }}>
      {backgroundSettings.hero_image && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: `rgba(255, 249, 242, ${backgroundSettings.hero_opacity})`,
          pointerEvents: 'none'
        }} />
      )}
      <div className="hero-content" style={{ position: 'relative', zIndex: 1 }}>
        <div className="hero-announcement">
          <Globe size={14} />
          <span>{content.announcement}</span>
        </div>

        <h1 className="heading-hero">
          {content.title}
        </h1>

        <p className="hero-subtitle body-large">
          {content.subtitle}
        </p>

        <div className="hero-cta">
          <button onClick={scrollToContact} className="btn-primary">
            {content.button_primary}
          </button>
          <button onClick={scrollToServices} className="btn-secondary">
            {content.button_secondary}
          </button>
        </div>

        <div className="hero-features">
          {content.features && content.features.map((feature, index) => (
            <div key={index} className="feature-item">
              <CheckCircle size={20} className="feature-icon" />
              <span className="body-small">{feature}</span>
            </div>
          ))}
        </div>

        {/* Stats Cards */}
        <div className="hero-stats-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '16px',
          marginTop: '48px',
          padding: '0 20px'
        }}>
          {statsArray.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div 
                key={index} 
                className="hero-stat-card"
                style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '20px 16px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                  transition: 'all 0.3s ease',
                  cursor: 'default',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
                }}
              >
                {/* Background decoration */}
                <div style={{
                  position: 'absolute',
                  top: '-15px',
                  right: '-15px',
                  width: '60px',
                  height: '60px',
                  background: stat.bgLight,
                  borderRadius: '50%',
                  opacity: 0.6
                }} />
                
                {/* Icon */}
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: stat.gradient,
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '12px',
                  position: 'relative',
                  zIndex: 1
                }}>
                  <Icon size={20} color="white" strokeWidth={2.5} />
                </div>

                {/* Number */}
                <div style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  background: stat.gradient,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  marginBottom: '6px',
                  position: 'relative',
                  zIndex: 1,
                  lineHeight: '1.2'
                }}>
                  {stat.number}
                </div>

                {/* Label */}
                <div style={{
                  fontSize: '11px',
                  color: '#64748b',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  position: 'relative',
                  zIndex: 1,
                  lineHeight: '1.3'
                }}>
                  {stat.label}
                </div>
              </div>
            );
          })}
        </div>

        {/* Mobile responsive styles */}
        <style>{`
          @media (max-width: 768px) {
            .hero-stats-grid {
              grid-template-columns: repeat(2, 1fr) !important;
              gap: 12px !important;
              margin-top: 32px !important;
            }
          }
          
          @media (max-width: 480px) {
            .hero-stats-grid {
              grid-template-columns: 1fr !important;
              padding: 0 16px !important;
            }
          }
        `}</style>
      </div>
    </section>
  );
};

export default Hero;